import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Button } from '@cbrebuild/blocks';

import EmptyState from '../../components/empty-state/empty-state';
import SearchInput from '../../nucleus/search/search-input';
import LoadingBar from '../../nucleus/loading-bar/loading-bar';
import { formatDealTypeAndRole } from '../../utils/deal-display-utils';

const AdminDealSearch = ({
  isSearching,
  searchResults,
  expandedDealId,
  totalResults,
  hasMore,
  currentPage,
  handleSearchChange,
  handleDealSelection,
  searchDeals,
  renderExpandedDetails // Function to render expanded details
}) => {
  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="search-loading">
          <LoadingBar />
          <p>Searching deals...</p>
        </div>
      );
    }

    if (searchResults.length === 0) {
      return null;
    }

    return (
      <div className="search-results">
        <div className="results-table">
          <div className="admin-table-header">
            <div className="admin-header-cell">DEAL</div>
            <div className="admin-header-cell">DEAL TYPE</div>
            <div className="admin-header-cell">DEAL STAGE</div>
            <div className="admin-header-cell">DEAL IQ ID</div>
            <div className="admin-header-cell">MTA DEAL ID</div>
            <div className="admin-header-cell">PURSUIT ID</div>
            <div className="admin-header-cell">CONNECTOR ID</div>
          </div>
          
          {searchResults.map((deal) => (
            <React.Fragment key={deal.id}>
              <div className={`admin-table-row ${expandedDealId === deal.id ? 'expanded' : ''}`}
                onClick={() => handleDealSelection(deal)}>
                <div className="admin-cell deal-cell">
                  <div className="deal-name-row" title={deal.deal_name}>
                    <span className="expand-icon-wrapper">
                      {expandedDealId === (deal.deal_iq_id || deal.id) ? (
                        <Icon iconName="chevron-up" className="expand-icon" />
                      ) : (
                        <Icon iconName="chevron-down" className="expand-icon" />
                      )}
                    </span>
                    <span className="deal-name-text">{deal.deal_name}</span>
                  </div>
                </div>
                <div className="admin-cell deal-type-cell">{formatDealTypeAndRole(deal.deal_type, deal.rep_role)}</div>
                <div className="admin-cell stage-cell">
                  <span className={`stage-badge ${deal.deal_life_cycle_stage?.toLowerCase()}`}>
                    {deal.deal_life_cycle_stage || '--'}
                  </span>
                </div>
                <div className="admin-cell deal-iq-id-cell">
                  {deal.deal_iq_id || deal.id || '--'}
                </div>
                <div className="admin-cell mta-deal-id-cell">
                  {deal.mta_deal_id || '--'}
                </div>
                <div className="admin-cell pursuit-cell">
                  {deal.pursuit_id || '--'}
                </div>
                <div className="admin-cell connector-cell">
                  {deal.connector_id || '--'}
                </div>
              </div>
              
              {/* Render expanded details using render prop */}
              {expandedDealId === (deal.original_deal_iq_id || deal.id) && renderExpandedDetails(deal)}
            </React.Fragment>
          ))}
        </div>
        
        {/* Results Summary and Load More */}
        {searchResults.length > 0 && (
          <div className="search-results-footer">
            <div className="results-summary">
              Showing {searchResults.length} of {totalResults} results
            </div>
            {hasMore && !isSearching && (
              <Button
                variant="outline"
                onClick={() => {
                  const searchInput = document.querySelector('.search-input input');
                  if (searchInput && searchInput.value) {
                    searchDeals(searchInput.value, currentPage + 1);
                  }
                }}
              >
                Load More Results
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="header-card">
        <div className="input-label">Search for Deals</div>
        <div className="search-deals">
          <SearchInput
            searchKey=""
            searchKeyAction="Enter deal name, Pursuit ID, Connector ID, MTA Deal ID, Property Name (minimum 3 characters)"
            handleSearchTermChange={handleSearchChange}
          />
          <div title="Enter deal name, Pursuit ID, Connector ID, MTA Deal ID, or Property Name to search and manage deals. Minimum 3 characters required.">
            <Icon iconName="info-circle" />
          </div>
        </div>
      </div>

      {!isSearching && searchResults.length === 0 && (
        <div className="empty-state-container">
          <EmptyState type="deals" title="Search for a deal or transaction to get started" />
        </div>
      )}

      {renderSearchResults()}
    </>
  );
};

AdminDealSearch.propTypes = {
  isSearching: PropTypes.bool.isRequired,
  searchResults: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  expandedDealId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalResults: PropTypes.number.isRequired,
  hasMore: PropTypes.bool.isRequired,
  currentPage: PropTypes.number.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  handleDealSelection: PropTypes.func.isRequired,
  searchDeals: PropTypes.func.isRequired,
  renderExpandedDetails: PropTypes.func.isRequired, // Render prop for expanded details
};

export default AdminDealSearch;
